import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { callAdminAPI } from "../api/callAdmin.js";
import { ordersApi } from "../api/orders.js";
import { queryKeys } from "../lib/queryKeys.js";

// ── Read hooks ───────────────────────────────────────────────────────────────

export function useCallLogs(params, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.logsList(params),
        queryFn: () => callAdminAPI.getLogs(params).then((r) => r.data),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        ...options,
    });
}

export function useQueue(params, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.queueList(params),
        queryFn: () => callAdminAPI.getQueue(params).then((r) => r.data),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        ...options,
    });
}

export function useQueueTypeCounts(baseParams, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.queueCounts(baseParams),
        queryFn: async () => {
            const [allRes, schedRes] = await Promise.all([
                callAdminAPI.getQueue({ ...baseParams, limit: 1 }),
                callAdminAPI.getQueue({ ...baseParams, limit: 1, call_type: "scheduled" }),
            ]);
            const all = allRes.data?.count || 0;
            const scheduled = schedRes.data?.count || 0;
            return { all, scheduled, normal: Math.max(0, all - scheduled) };
        },
        staleTime: 30_000,
        ...options,
    });
}

export function useCrmUsers(options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.crmUsers(),
        queryFn: () => callAdminAPI.getCrmUsers().then((r) => r.data?.data || []),
        staleTime: 5 * 60_000,
        ...options,
    });
}

export function useOverviewStats(date, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.overview(date),
        queryFn: async () => {
            const [usersRes, queueRes, logsRes, ordersRes] = await Promise.all([
                callAdminAPI.getCrmUsers(),
                callAdminAPI.getQueue({ date, limit: 10000 }),
                callAdminAPI.getLogs({ date_from: date, date_to: date, limit: 1000 }),
                ordersApi.getAll({ date_from: date, date_to: date, limit: 1000 }),
            ]);

            const users = usersRes.data?.data || [];
            const summary = queueRes.data?.summary || [];
            const queueData = queueRes.data?.data || [];
            const logs = logsRes.data?.data || [];
            const ordersData = Array.isArray(ordersRes) ? ordersRes : (ordersRes?.data || []);

            const assignedByUser = {};
            queueData.forEach((e) => {
                const uid = e.crmUser?.id || e.crm_user_id;
                if (uid) assignedByUser[uid] = (assignedByUser[uid] || 0) + 1;
            });

            const logCountByUser = {};
            logs.forEach((l) => {
                const uid = l.user?.id || l.user_id;
                if (uid) logCountByUser[String(uid)] = (logCountByUser[String(uid)] || 0) + 1;
            });

            const orderCountByUser = {};
            ordersData.forEach((o) => {
                const uid =
                    o.placed_by || o.Creator?.id || o.creator?.id ||
                    o.crm_user_id || o.user?.id || o.user_id;
                if (uid) orderCountByUser[String(uid)] = (orderCountByUser[String(uid)] || 0) + 1;
            });

            return users.map((user) => {
                const s = summary.find((s) => s.user_id === user.id) || {};
                const total =
                    assignedByUser[user.id] ??
                    s.total ??
                    (s.pending ?? 0) + (s.called ?? 0) + (s.ordered ?? 0) + (s.skipped ?? 0);
                return {
                    user_id: user.id,
                    name: user.name,
                    role: user.role?.name ?? "",
                    total,
                    pending: s.pending ?? 0,
                    callsDone: logCountByUser[String(user.id)] || 0,
                    ordersPlaced: orderCountByUser[String(user.id)] || 0,
                };
            });
        },
        staleTime: 60_000,
        ...options,
    });
}

export function useAgentQueue(params, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.agentQueue(params),
        queryFn: () => callAdminAPI.getQueue(params).then((r) => r.data),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        ...options,
    });
}

export function useAgentLogs(params, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.agentLogs(params),
        queryFn: () => callAdminAPI.getLogs(params).then((r) => r.data),
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        ...options,
    });
}

export function useAgentOrders(params, options = {}) {
    return useQuery({
        queryKey: queryKeys.callAdmin.agentOrders(params),
        queryFn: async () => {
            const data = await ordersApi.getAll(params);
            const arr = Array.isArray(data) ? data : (data?.data || []);
            return { data: arr, count: data?.count ?? arr.length, totalPages: data?.totalPages || 1 };
        },
        staleTime: 30_000,
        placeholderData: keepPreviousData,
        ...options,
    });
}

// ── Mutation hooks ───────────────────────────────────────────────────────────

export function useAddToQueue() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: callAdminAPI.addToQueue,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.callAdmin.queue() }),
    });
}

export function useRedistribute() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: callAdminAPI.redistribute,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.callAdmin.queue() }),
    });
}

export function useRunPopulate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: callAdminAPI.runPopulate,
        onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.callAdmin.all() }),
    });
}
